/** @prettier */
import { List, Map } from 'immutable';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import Currency from '@xanadu/ui-shared/components/Currency';
import Button from '@xanadu/ui-shared/components/widgets/Button';
import NativeDropdown from '@xanadu/ui-shared/components/widgets/NativeDropdown';
import Spinner from '@xanadu/ui-shared/components/widgets/Spinner';
import { BETSLIP_PRODUCTS, DEFAULT_BONUS_DROPDOWN_OPTIONS } from '@xanadu/ui-shared/constants';
import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { Multiple as MultipleRecord } from '@xanadu/ui-shared/modules/multiples/reducer';

import { PERMUTATION_TYPES, PRODUCTS } from '../../../constants';

import Selection from '../../../containers/Multiples/Selection';

import { currency as localiseCurrency } from '../../../utils/localise';

import IconReloadBets from '../../../../images/icons/misc/icon-reloadbets.svg';

import selectionStyle from '../Selection/Selection.module.scss';
import ZeroRestricted from '../ZeroRestricted';

import styles from './BuildView.module.scss';

const LoadingDots = () => (
  <React.Fragment>
    <span className={styles.loadingOdds}>.</span>
    <span className={styles.loadingOdds}>.</span>
    <span className={styles.loadingOdds}>.</span>
  </React.Fragment>
);

const getErrors = (errors = {}, selections) => {
  let hasErrors = Object.values(errors).some((e) => e && e.invalid);

  if (selections && selections.size < 2) {
    hasErrors = true;
  }

  return hasErrors;
};

const getErrorsMessages = (errors) =>
  Object.keys(errors)
    .map((id) => errors[id]?.message)
    .filter(Boolean);

const truncateNumber = (value, decimalPlaces) => value.toFixed(decimalPlaces + 1).slice(0, -1);

const CASH_BET = DEFAULT_BONUS_DROPDOWN_OPTIONS[0].value;

const BuildView = ({
  multipleBuild,
  selectedBonusId,
  previousMultipleBuild,
  submitMultiple,
  resetSubmitMultipleError,
  setSelectionErrors,
  removeOffersByCids,
  clearMultipleSelections,
  reloadPreviousSelections,
  resetTotalOdds,
  requestTotalOdds,
  requestPermutations,
  setPermutationType,
  setMultiplesBonusId,
  setInitialStake,
  buildLoading,
  buildSubmitted,
  requestError,
  currency,
  product,
  freeBets,
  bonusAllowed,
  initialStake,
  selectionErrors,
  removeSelectionError,
  allEvents,
  resetSelectedBonusId,
}) => {
  const {
    selections,
    odds: totalOdds,
    minStake,
    maxStake,
    oddsLoading,
    apiError: totalOddsError,
    permutations,
    permutationType,
  } = multipleBuild;

  const [stake, setStake] = useState('');
  const [totalOddsLoading, setTotalOddsLoading] = useState(false);
  const [reloadVisible, setReloadVisible] = useState('');
  const [bonusValue, setBonusValue] = useState(selectedBonusId || CASH_BET);
  const [useBonus, setUseBonus] = useState(false);
  const selectionsErrors = getErrors(selectionErrors, selections);

  // refactor this in the next iteration so
  // that selections include sportId, competitionId and sportId directly in Redux multiples store.
  const eventsByselections = useMemo(() => {
    return selections.reduce((acc, selection) => {
      const { eventId } = selection;
      const event = allEvents && allEvents.get(eventId);

      if (event) {
        return [...acc, event];
      }

      return acc;
    }, []);
  }, [selections, allEvents]);

  const extractMetaTags = (event) => {
    let sportId = null;
    let competitionId = null;
    let locationId = null;

    event.metaTags.forEach((tag) => {
      const type = tag.get('type')?.toUpperCase();
      const value = tag.get('id');

      if (type === 'SPORT') sportId = value;
      if (type === 'COMPETITION') competitionId = value;
      if (type === 'LOCATION') locationId = value;
    });

    return { sportId, competitionId, locationId };
  };

  const computedBonusDropdown = useMemo(() => {
    if (freeBets && freeBets.size) {
      return [
        ...DEFAULT_BONUS_DROPDOWN_OPTIONS,
        ...freeBets
          .map(({ id, amount, currency: curr, name, products, criteria }) => {
            return {
              id,
              name: `${localiseCurrency(amount, curr, { symbol: true })} ${name}`,
              products,
              criteria: criteria.find((c) => c.type !== 'PRODUCT'),
            };
          })
          .filter(({ criteria, products }) => {
            return eventsByselections.every((event) => {
              const { sportId, competitionId, locationId } = extractMetaTags(event);

              const criteriaMatches =
                !criteria ||
                [event.id, sportId, competitionId, locationId].includes(criteria.value);

              return criteriaMatches && products.includes(BETSLIP_PRODUCTS.multiples);
            });
          }),
      ];
    }
    return DEFAULT_BONUS_DROPDOWN_OPTIONS;
  }, [freeBets ? freeBets.size : 0, eventsByselections]);

  useEffect(() => {
    if (bonusValue !== selectedBonusId) {
      resetSelectedBonusId();
    }
  }, [bonusValue]);

  const actionsClass = [
    styles.actions,
    selections.size > 1 && permutations.size ? styles['actions--visible'] : '',
  ];

  const reloadSelectionsClass = [
    styles.reloadSelections,
    reloadVisible ? styles['reloadSelections--visible'] : '',
  ].join(' ');

  const permutationsMap = permutations.reduce((acc, item) => {
    const { permutationName: name, permutationType: value, combinations } = item;

    return {
      ...acc,
      [value]: {
        name,
        value,
        combinations,
      },
    };
  }, {});

  const permutationsMapHash = Object.keys(permutationsMap).join('_');

  const handleSetPermutationType = (options = {}) => {
    const { value } = options;

    setTotalOddsLoading(true);
    setPermutationType(value);
    setUseBonus(value === PERMUTATION_TYPES.ACCUMULATOR && bonusValue !== 'NONE');
  };

  const onUpdateStake = (amount) => {
    setStake(amount);
    setInitialStake(amount);
  };

  const handleUseBonus = (options = {}) => {
    const { value } = options;
    const selectedValue = value !== 'NONE';

    if (selectedValue) {
      const selectedOption = freeBets.find((bet) => bet.id.toString() === value) || {};
      onUpdateStake(selectedOption.amount);
      setMultiplesBonusId(value);
    } else {
      onUpdateStake('');
    }

    setBonusValue(value);
    setUseBonus(Boolean(selectedValue));
    setMultiplesBonusId((selectedValue && value) || '');
  };

  useEffect(() => {
    setStake(initialStake);
  }, [initialStake]);

  useEffect(() => {
    if (selectedBonusId) {
      const selectedOption = freeBets.find((bet) => bet.id.toString() === selectedBonusId);
      onUpdateStake(selectedOption.amount);
    } else if (!initialStake) {
      onUpdateStake('');
    }
  }, [selectedBonusId, freeBets]);

  useEffect(() => {
    setReloadVisible(previousMultipleBuild && !selections.size);
  }, [previousMultipleBuild, selections.size]);

  useEffect(() => {
    if (!selectionsErrors) {
      requestTotalOdds();
    }
  }, [selectionsErrors]);

  useEffect(() => {
    setUseBonus(selectedBonusId && selectedBonusId !== 'NONE');
    setBonusValue(selectedBonusId || 'NONE');
  }, [selectedBonusId]);

  useEffect(() => {
    if (selections.size > 1) {
      requestPermutations();
      requestTotalOdds();
    } else {
      resetTotalOdds();
      setMultiplesBonusId('');
      setBonusValue('NONE');
      onUpdateStake('');
    }

    const containerSelections = document.getElementsByClassName(styles.containerSelections)[0];

    if (containerSelections) {
      const dimensions = containerSelections.getClientRects();

      if (dimensions[0] && dimensions[0].height) {
        containerSelections.scrollTop = dimensions[0].height;
      }
    }
  }, [selections.size]);

  useEffect(() => {
    if (!permutationsMap[permutationType] && Object.keys(permutationsMap).length > 0) {
      handleSetPermutationType();
    }
  }, [permutationsMapHash, permutationType]);

  useEffect(() => {
    if (buildSubmitted) {
      onUpdateStake('');
    }
  }, [buildSubmitted]);

  useEffect(() => {
    setTotalOddsLoading(false);
  }, [totalOdds]);

  const stakeError = useMemo(() => {
    if (!selectionsErrors && maxStake && parseFloat(stake) > maxStake) {
      return {
        label: 'max',
        value: maxStake,
      };
    } else if (!selectionsErrors && parseFloat(stake) < minStake) {
      return {
        label: 'minimum',
        value: minStake,
      };
    }

    return '';
  }, [stake, maxStake, minStake, selectionsErrors]);

  const handleSubmit = useCallback(() => {
    if (getErrors(selections) || stake === '' || stakeError || buildLoading) {
      return;
    }

    submitMultiple({
      stake,
      'use-bonus': useBonus,
      ...(useBonus && { 'bonus-id': bonusValue }),
    });

    track('Accas', 'Multiple submission', {
      description: `Odds: ${totalOdds} | N.legs: ${selections.size} | Stake: ${stake} | Perm Type: ${permutationType}`,
    });

    setMultiplesBonusId('');
  }, [selections, stake, stakeError.toString(), buildLoading]);

  const handleClearAll = () => {
    setStake('');

    const offersToRemove = selections.map(({ cid }) => cid);
    removeOffersByCids(offersToRemove);

    clearMultipleSelections();
    resetSubmitMultipleError();
  };

  const handleStakeKeyDown = (e) => {
    const code = e.keyCode;

    if (code === 13) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleStake = (e) => {
    let { value } = e.target;

    value = value.replace(/[^0-9.]/g, '');

    const decimalCount = (value.match(/\./g) || []).length;
    if (decimalCount > 1) {
      value = value.split('.').slice(0, 2).join('.');
    }

    const parts = value.split('.');
    if (parts[1] && parts[1].length > 2) {
      parts[1] = parts[1].slice(0, 2);
      value = parts.join('.');
    }

    onUpdateStake(value);
    resetSubmitMultipleError();
  };

  const onUpdateSelection = useCallback((action, selectionId, invalid, message) => {
    if (action === 'add' || action === 'update') {
      setSelectionErrors({
        selectionId,
        error: { invalid, message },
      });
    }

    if (action === 'remove') {
      removeSelectionError(selectionId);
    }
  }, []);

  const selectedPermutation = permutationsMap[permutationType] || {};
  const numberOfCombinations = selectedPermutation.combinations || 1;
  const totalStake = numberOfCombinations ? parseFloat(stake * numberOfCombinations) : stake;

  let returnValue = '0.00';
  let errorMessageClass = '';
  let totalOddsValue = 'n/a';

  if (!selectionsErrors && totalOdds) {
    totalOddsValue = totalOddsLoading ? <LoadingDots /> : truncateNumber(totalOdds, 2);

    returnValue = <Currency value={totalStake * totalOdds} precision={2} currency={currency} />;
  }

  if (requestError || stakeError || totalOddsError) {
    errorMessageClass = styles['containerErrorMessage--visible'];
  }

  const showFreeBet =
    bonusAllowed && computedBonusDropdown.length > 0 && numberOfCombinations === 1;

  const disableStake = Boolean(
    numberOfCombinations === 1 && selectedBonusId && selectedBonusId !== 'NONE'
  );

  const hasErrors = selectionsErrors || totalOddsError || stakeError !== '';

  const disableSubmitButton = hasErrors || Number(stake) <= 0;

  return (
    <div className={styles.main}>
      <div className={styles.containerSelections}>
        {product === PRODUCTS.mbzero && selections.size < 1 && <ZeroRestricted />}
        <TransitionGroup component="div" className={styles.selections}>
          {selections.map(({ cid, runnerId, side, requestError: error }, i) => (
            <CSSTransition
              key={cid}
              classNames={{
                enter: selectionStyle['main--enter'],
                enterActive: selectionStyle['main--enter-active'],
                enterDone: selectionStyle['main--enter-done'],
                exit: selectionStyle['main--exit'],
                exitActive: selectionStyle['main--exit-active'],
                exitDone: selectionStyle['main--exit-done'],
              }}
              timeout={200}
            >
              <Selection
                cid={cid}
                runnerId={runnerId}
                side={side}
                requestError={error}
                onUpdateSelection={onUpdateSelection}
                index={i + 1}
              />
            </CSSTransition>
          ))}
        </TransitionGroup>
        {previousMultipleBuild && !selections.size && (
          <button className={reloadSelectionsClass} onClick={reloadPreviousSelections}>
            <IconReloadBets />
            <span>Reload Last Selections</span>
          </button>
        )}
        {selections.size === 1 && (
          <div className={styles.oneSelectionMessage}>Add more selections to create a Multiple</div>
        )}
      </div>
      {(requestError || totalOddsError) && (
        <div className={`${styles.containerErrorMessage} ${errorMessageClass}`}>
          {requestError || totalOddsError}
        </div>
      )}
      {stakeError && (
        <div className={`${styles.containerErrorMessage} ${errorMessageClass}`}>
          <span>{`The ${stakeError.label} bet you can place is`}&nbsp;</span>
          <Currency value={stakeError.value} currency={currency} precision={2} />
        </div>
      )}
      <div className={actionsClass.join(' ')}>
        <div className={styles.header}>
          <span>{`Multiples (${selections.size} selections)`}</span>
          <span>Stake</span>
          <span>Return</span>
        </div>
        <div className={styles.details}>
          <div className={styles.detail}>
            <NativeDropdown
              name="permutation"
              options={Object.values(permutationsMap)}
              onChange={handleSetPermutationType}
              value={permutationType}
              dir="ltl"
            />
          </div>
          <div className={`${styles.detail} ${styles['detail--stake']}`}>
            <input
              type="text"
              name="stake"
              autoComplete="off"
              data-hook="multiples-stake"
              className={styles.input}
              onChange={handleStake}
              onKeyDown={handleStakeKeyDown}
              value={stake}
              placeholder="0.00"
              disabled={disableStake}
            />
          </div>
          <div className={`${styles.detail} ${styles['detail--return']}`}>
            {oddsLoading && stake ? (
              <Spinner size="small" theme="dark" className={styles.oddsLoadingSpinner} />
            ) : (
              <div className={styles.detailValue}>{returnValue}</div>
            )}
          </div>
        </div>
        <div className={styles.bonusRow}>
          <div className={styles.bonusesDetail}>
            {showFreeBet && computedBonusDropdown.length > 1 && (
              <NativeDropdown
                name="bonus"
                options={computedBonusDropdown}
                onChange={handleUseBonus}
                value={bonusValue}
                dir="ltl"
              />
            )}
          </div>
          <div className={styles.totalStake}>
            {permutationType !== PERMUTATION_TYPES.ACCUMULATOR ? (
              <React.Fragment>
                <span>Total Stake: </span>
                <Currency value={totalStake} precision={2} currency={currency} />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <span className={styles.oddsLabel}>Odds</span> <br />
                <span className={styles.odds}>{totalOddsValue}</span>
              </React.Fragment>
            )}
          </div>
        </div>
        <div className={styles.buttons}>
          <div className={styles.containerButton}>
            <Button
              className={styles.button}
              sizeType="wider"
              buttonType="plain-white"
              onClick={handleClearAll}
              title="Clear Multiple"
              dataHook="multiples-clearall"
            >
              clear all
            </Button>
          </div>
          <div
            className={styles.containerButton}
            role="button"
            onClick={() => {
              let reason = '';

              if (selectionsErrors) {
                const messages = getErrorsMessages();
                reason = `Selection Errors: ${messages.join(', ')}`;
              } else if (stakeError !== '') {
                reason = 'Max stake error';
              } else if (stake === '') {
                reason = 'Missing stake';
              }

              if (reason) {
                track('Accas', 'Click on disabled submit', {
                  description: `Reason: ${reason}`,
                });
              }
            }}
          >
            <Button
              className={styles.button}
              sizeType="wider"
              buttonType="primary"
              onClick={handleSubmit}
              loading={buildLoading}
              success={buildSubmitted}
              dataHook="multiples-submit"
              title="Submit Multiple"
              disabled={disableSubmitButton}
            >
              {requestError ? 'try again' : 'submit'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

BuildView.propTypes = {
  multipleBuild: PropTypes.instanceOf(MultipleRecord).isRequired,
  selectedBonusId: PropTypes.string,
  previousMultipleBuild: PropTypes.instanceOf(MultipleRecord),
  clearMultipleSelections: PropTypes.func.isRequired,
  removeOffersByCids: PropTypes.func.isRequired,
  resetTotalOdds: PropTypes.func.isRequired,
  submitMultiple: PropTypes.func.isRequired,
  setSelectionErrors: PropTypes.func.isRequired,
  removeSelectionError: PropTypes.func.isRequired,
  resetSubmitMultipleError: PropTypes.func.isRequired,
  requestTotalOdds: PropTypes.func.isRequired,
  resetSelectedBonusId: PropTypes.func.isRequired,
  requestPermutations: PropTypes.func.isRequired,
  setPermutationType: PropTypes.func.isRequired,
  reloadPreviousSelections: PropTypes.func.isRequired,
  buildLoading: PropTypes.bool.isRequired,
  buildSubmitted: PropTypes.bool.isRequired,
  requestError: PropTypes.string.isRequired,
  setMultiplesBonusId: PropTypes.func.isRequired,
  setInitialStake: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  product: PropTypes.string.isRequired,
  freeBets: PropTypes.instanceOf(List),
  allEvents: PropTypes.instanceOf(Map),
  bonusAllowed: PropTypes.bool.isRequired,
  initialStake: PropTypes.oneOfType(['string', 'number']),
  selectionErrors: PropTypes.instanceOf(Object),
};

export default memo(BuildView);
